// QuestionConfig.tsx
import {
    BicycleTypeEnum,
    EBike,
    FootPosition,
    FootProblems,
    FootType,
    HandProblems,
    LegAxis,
    PelvicProblems,
    RidingDuration,
    RidingFrequency,
    Sex,
} from "./AnswerEnums";
import {BicycleType} from "./BicycleType";
// import {convertEuToUsShoeSize} from "../utils/sizeConversions.ts";

export interface AnswerConfig {
    group?: string; // Neue Option zur Gruppierung
    text: string;
    value?: number | string;
    image?: string;
    min?: number;
    max?: number;
    step?: number;
}

export enum QuestionType {
    singleChoice = "singleChoice",
    multiChoice = "multiChoice",
    numeric = "numeric",
    multiNumeric = "multiNumeric", // Add this new type
}


/// For Slider Questions
export const SliderQuestions = ["hand_size", "shoe_size", "body_measurements"];
export interface InputConfig {
    inputId: string;
    label: string;
    value?: number
    min: number;
    max: number;
    minNormal?: number;
    maxNormal?: number;
    step: number;
    defaultValue: number;
    unit: string;
    image?: string;
}



export interface QuestionConfig {
    matrixId: number;

    questionId: string;

    questionHeadline: string;

    description: string;

    infoText: boolean;

    detailedInfoPopupDescription?: string;

    helpTextDocumentName: string;

    canSelectMultipleOptions: boolean;

    displayPlaceholder: boolean;

    defaultValue: (answer: Record<string, (string | number)[]>) => (string | number)[];

    answers: AnswerConfig[];

    filterAnswer?: (answers: Record<string, (string | number)[]>) => (answer: AnswerConfig) => boolean;

    optional: boolean;

    questionType: QuestionType;

    inputConfigs?: InputConfig[];
    additionalDisplay?: (value: number) => string;
}

interface GenderConfig  {
    standardHeight: number;
    standardShoeSize: number;
    hasPubicBone: boolean;
    hasProstate: boolean;
}


const GenderSpecificConfig: Record<Sex, GenderConfig> = {
    male: {
        standardHeight: 180,
        standardShoeSize: 42,
        hasPubicBone: false,
        hasProstate: true,
    },
    female: {
        standardHeight: 165,
        standardShoeSize: 39,
        hasPubicBone: true,
        hasProstate: false,
    },
    divers: {
        standardHeight: 173,
        standardShoeSize: 40,
        hasPubicBone: true,
        hasProstate: true,
    },
};

export function enumToAnswers(enumeration: object): AnswerConfig[] {
    return (
        Object.values(enumeration).map((v) => ({
            text: v as string,
        })) ?? []
    );
}

function enumToAnswersWithImages(enumeration: object, imgPrefix: string): AnswerConfig[] {
    return (
        Object.values(enumeration).map((v) => ({
            text: v as string,
            image: `${imgPrefix}/${v as string}.jpg`,
        })) ?? []
    );
}

export const QuestionnaireConfig: Record<string, QuestionConfig> = {
    sex: {
        matrixId: 9,
        questionId: "sex",
        description: "sex_question_description",
        questionHeadline: "sex_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [Sex.male],
        answers: [{ text: Sex.male }, { text: Sex.female }],
        optional: false,
        questionType: QuestionType.singleChoice,
        infoText: false,
    },
    bicycle_type: {
        matrixId: 1,
        questionId: "bicycle_type",
        description: "bicycle_type_question_description",
        questionHeadline: "bicycle_type_question_question_text",
        infoText: true,
        helpTextDocumentName: "bike_types",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [BicycleType.MTB_ALL_MOUNTAIN.internalId],
        answers: enumToAnswers(BicycleTypeEnum),
        optional: false,
        questionType: QuestionType.singleChoice,
        detailedInfoPopupDescription: "bicycle_type_question_infopopup_text"
    },
    e_bike: {
        matrixId: 2,
        questionId: "e_bike",
        description: "e_bike_question_description",
        questionHeadline: "ebike_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: (answers) => {
            const bicycleType = answers["bicycle_type"]?.[0] as BicycleTypeEnum;

            console.log("AAA: ", bicycleType);

            if (
                [BicycleTypeEnum.triathlon, BicycleTypeEnum.racingBike, BicycleTypeEnum.gravel].includes(bicycleType)
            ) {
                return [EBike.eBikeNo];
            } else {
                return [EBike.eBikeYes];
            }
        },
        answers: enumToAnswers(EBike),
        optional: false,
        questionType: QuestionType.singleChoice,
        infoText: false,
    },
    driving_frequency: {
        matrixId: 6,
        questionId: "driving_frequency",
        description: "driving_frequency_question_description",
        questionHeadline: "driving_frequency_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [RidingFrequency.oncePerWeek],
        answers: enumToAnswers(RidingFrequency),
        optional: false,
        questionType: QuestionType.singleChoice,
        infoText: false,
    },
    driving_duration: {
        matrixId: 7,
        questionId: "driving_duration",
        description: "driving_duration_question_description",
        questionHeadline: "driving_duration_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [RidingDuration.lessThan2Hours],
        answers: enumToAnswers(RidingDuration),
        optional: false,
        questionType: QuestionType.singleChoice,
        infoText: false,
    },
    weight: {
        matrixId: 8,
        questionId: "weight",
        description: "weight_question_description",
        questionHeadline: "weight_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [80],
        answers: [],
        inputConfigs: [
            {
                inputId: "weight",
                label: "weight_kg_label",
                min: 55,
                max: 140,
                step: 1,
                defaultValue: 65,
                unit: "cm",
                image: "/images/bodyMeasurements/height.png",
            },
        ],
        optional: false,
        questionType: QuestionType.numeric,
        infoText: false,
    },
    pelvic_problems: {
        matrixId: 10,
        questionId: "pelvic_problems",
        description: "pelvic_problems_question_description",
        questionHeadline: "pelvic_problems_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: true,
        displayPlaceholder: true,
        defaultValue: () => [],
        answers: enumToAnswers(PelvicProblems),
        optional: true,
        filterAnswer(answers) {
            const sex = (answers["sex"]?.[0] as Sex) ?? Sex.male;
            const genderConfig = GenderSpecificConfig[sex];
            return (answer: AnswerConfig) => {
                if (answer.text === PelvicProblems.prostateProblem) {
                    return genderConfig.hasProstate;
                }
                if (answer.text === PelvicProblems.pubicBoneProblem) {
                    return genderConfig.hasPubicBone;
                }
                return true;
            };
        },
        questionType: QuestionType.multiChoice,
        infoText: true,
        detailedInfoPopupDescription: "pelvic_problems_question_infopopup_text"
    },
    hand_problems: {
        matrixId: 12,
        questionId: "hand_problems",
        description: "hand_problem_question_description",
        questionHeadline: "hand_problem_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: true,
        displayPlaceholder: true,
        defaultValue: () => [],
        answers: enumToAnswers(HandProblems),
        optional: true,
        questionType: QuestionType.multiChoice,
        infoText: true,
        detailedInfoPopupDescription: "hand_problem_question_infopopup_text"
    },
    leg_axis: {
        matrixId: 14,
        questionId: "leg_axis",
        description: "leg_axis_question_description",
        questionHeadline: "leg_axis_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [LegAxis.straightLegs],
        answers: enumToAnswersWithImages(LegAxis, "/src/assets/images/question_images/leg_axis"),
        optional: true,
        questionType: QuestionType.singleChoice,
        infoText: false,
    },
    foot_type: {
        matrixId: 13,
        questionId: "foot_type",
        description: "foot_type_question_description",
        questionHeadline: "foot_type_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [FootType.normalFootType],
        answers: enumToAnswersWithImages(FootType, "/src/assets/images/question_images/foot_type"),
        optional: true,
        questionType: QuestionType.singleChoice,
        infoText: true,
        detailedInfoPopupDescription: "foot_type_question_infopopup_text"
    },
    foot_position: {
        matrixId: 15,
        questionId: "foot_position",
        description: "foot_position_question_description",
        questionHeadline: "foot_position_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: false,
        displayPlaceholder: true,
        defaultValue: () => [FootPosition.vFootPosition],
        answers: enumToAnswersWithImages(FootPosition, "/src/assets/images/question_images/foot_position"),
        optional: true,
        questionType: QuestionType.singleChoice,
        infoText: true,
    },
    foot_problems: {
        matrixId: 17,
        questionId: "foot_problems",
        description: "foot_problem_question_description",
        questionHeadline: "foot_problem_question_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: true,
        displayPlaceholder: true,
        defaultValue: () => [],
        answers: enumToAnswers(FootProblems),
        optional: true,
        questionType: QuestionType.multiChoice,
        infoText: true,
        detailedInfoPopupDescription: "foot_problem_question_infopopup_text"
    },
    sitbone_distance: {
        matrixId: 3,
        questionId: "sitbone_distance",
        description: "sitbone_distance_question_description",
        questionHeadline: "seat_bone_distance_question_question_text",
        helpTextDocumentName: "seatbones_measurement",
        canSelectMultipleOptions: false,
        displayPlaceholder: false,
        defaultValue: () => [],
        answers: [], // No predefined answers, as the value is numeric or entered by the user
        optional: false,
        questionType: QuestionType.numeric,
        inputConfigs: [
            {
                inputId: "seat_bone_distance_cm",
                label: "question_label_sitbone_distance",
                min: 5,
                max: 20,
                minNormal: 10,
                maxNormal: 15,
                step: 1,
                defaultValue: 12.0,
                unit: "cm",
            }
        ],
        infoText: true,
        detailedInfoPopupDescription: "sitbone_distance_question_infopopup_text"
    },
    hand_size_measurement: {
        matrixId: 18,
        questionId: "hand_size_measurement",
        description: "hand_size_question_description",
        questionHeadline: "hand_size_question_question_text",
        helpTextDocumentName: "hand_size_help",
        canSelectMultipleOptions: false,
        displayPlaceholder: false,
        defaultValue: () => [],
        answers: [], // No predefined answers
        optional: false,
        questionType: QuestionType.multiNumeric,
        inputConfigs: [
            {
                inputId: "grip_width_cm",
                label: "grip_width_label",
                min: 7,
                max: 20,
                minNormal: 10,
                maxNormal: 15,
                step: 0.5,
                defaultValue: 15.0,
                unit: "cm",
            },
            {
                inputId: "hand_width_cm",
                label: "hand_width_label",
                min: 5,
                max: 12,

                minNormal: 7,
                maxNormal: 10,
                step: 0.5,
                defaultValue: 8.0,
                unit: "cm",
            },
        ],
        infoText: true,
        detailedInfoPopupDescription: "hand_size_question_infopopup_text"
    },
    body_measurements: {
        matrixId: 20,
        questionId: "body_measurements",
        description: "body_measurements_question_description",
        questionHeadline: "body_measurements_question_question_text",
        helpTextDocumentName: "body_measurements_help",
        canSelectMultipleOptions: false,
        displayPlaceholder: false,
        defaultValue: () => [],
        answers: [],
        optional: false,
        questionType: QuestionType.multiNumeric,
        inputConfigs: [
            {
                inputId: "body_height",
                label: "body_height_label",
                min: 131,
                max: 239,
                minNormal: 150,
                maxNormal: 210,
                step: 1,
                defaultValue: 175,
                unit: "cm",
                image: "/images/bodyMeasurements/height.png",
            },
            // {
            //     inputId: "arm_length_cm",
            //     label: "arm_length_label",
            //     min: 50,
            //     max: 75,
            //     step: 1,
            //     defaultValue: 45,
            //     unit: "cm",
            //     image: "/images/bodyMeasurements/shoulder_width.png",
            // },
            {
                inputId: "shoulder_width_cm",
                label: "shoulder_width_label",
                min: 30,
                max: 55,
                minNormal: 40,
                maxNormal: 50,
                step: 1,
                defaultValue: 45,
                unit: "cm",
                image: "/images/bodyMeasurements/shoulder_width.png",
            },
            // {
            //     inputId: "step_length",
            //     label: "step_length_label",
            //     min: 55,
            //     max: 110,
            //     step: 1,
            //     defaultValue: 70,
            //     unit: "cm",
            //     image: "/images/bodyMeasurements/step_length.png",
            // },
            {
                inputId: "hip_circumference", // TODO: Change to hip_circumference
                label: "hip_circumference_label",
                min: 70,
                max: 160,
                minNormal: 100,
                maxNormal: 130,
                step: 1,
                defaultValue: 110,
                unit: "cm",
                image: "/images/bodyMeasurements/hip_width.png",
            },
        ],
        infoText: false,
        detailedInfoPopupDescription: "body_measurements_question_infopopup_text"
    },
    shoe_size_measurement: {
        matrixId: 19,
        questionId: "shoe_size_measurement",
        description: "shoe_size_question_description",
        questionHeadline: "shoe_size_question_question_text",
        helpTextDocumentName: "shoe_size_help",
        canSelectMultipleOptions: false,
        displayPlaceholder: false,
        defaultValue: () => [],
        answers: [],
        optional: false,
        questionType: QuestionType.numeric,
        inputConfigs: [
            {
                inputId: "shoe_size_eu",
                label: "shoe_size_eu_label",
                min: 36,
                max: 50,
                minNormal: 42,
                maxNormal: 55,
                step: 1,
                defaultValue: 42.0,
                unit: "EU",
            },
        ],
        additionalDisplay: (value) => {
            // round value to integer
            const roundedValue = Math.round(value);
            return `${roundedValue} EU`;
        },
        infoText: false,
    },
    privacy_agreement: {
        // this question has two fields which are going to be filled: email and name,
        // therefore we use two questionIds "email" and "name", instead of "privacy_agreement"
        matrixId: 100,
        questionId: "privacy_agreement",
        description: "privacy_agreement_question_description",
        questionHeadline: "privacy_agreement_question_text",
        helpTextDocumentName: "",
        canSelectMultipleOptions: true,
        displayPlaceholder: false,
        defaultValue: () => [],
        answers: [],
        optional: false,
        questionType: QuestionType.singleChoice,
        infoText: false,
    }
};